import { useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";

/**
 * Where Stripe returns a buyer who has just signed up (KEY-368) — or, since KEY-634 and KEY-635, somebody
 * already signed in who has just reactivated an expired trial or added a card to a running one.
 *
 * Deliberately says nothing about what was bought. This page is reached with whatever session the browser
 * already had — for a new buyer, usually none — so it has no authenticated way to look the purchase up, and
 * Stripe's redirect carries only a session id. Claiming "you're on Commercial Silver" from an unverified query
 * parameter would be a page that lies whenever somebody edits the URL.
 *
 * <b>And it must not claim money changed hands.</b> A card-free trial (KEY-431) takes no payment at all,
 * so "your payment went through" was false for every one of them — and at an expo, where every sign-up
 * comes off a QR code onto a card-free tier, it was false for everybody. The same reasoning that stops
 * this page naming the plan stops it knowing whether anything was charged, so the wording below is simply
 * true either way rather than guessing from a query parameter. The `/join/` page, which DOES know the
 * tier, is where the specific promise about cards is made.
 *
 * <b>Nor may it promise an email that was not sent (KEY-638).</b> The welcome email goes to a brand-new
 * organisation only; a checkout started from inside the app has no email behind it, so a signed-in reader
 * is told where their plan now lives instead.
 *
 * The purchase itself is completed by the webhook, not by this redirect. A buyer who closes the tab on the
 * checkout page still gets everything; this page only tells them what happens next. For a reactivation
 * that has a sharp edge: until the webhook lands, /api/me still says the trial is over, and "Go to your
 * dials" would put them straight back on the trial-ended screen. So while the gate is still down this page
 * re-reads /api/me every couple of seconds, and offers the door once it lifts — or after half a minute
 * regardless, with a word about why it might not open first time.
 */
export function PurchaseWelcome() {
  const { me, refreshMe } = useAuth();
  const gated = !!me?.trialExpired;
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (!gated) {
      return;
    }
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      void refreshMe();
      if (tries >= 15) {
        clearInterval(timer);
        setGaveUp(true);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [gated, refreshMe]);

  const settingUp = gated && !gaveUp;

  return (
    <main className="buy-page">
      <header className="buy-header">
        <h1>Thank you — you're all set</h1>
        <p className="buy-muted">
          {me
            ? "Stripe is telling us now, which usually takes a few seconds."
            : "We're setting your account up now, which usually takes a few seconds."}
        </p>
      </header>

      <section className="buy-checkout">
        {me ? (
          <p>
            Your plan — what it costs, when it's next due, and the card it's on — is under Your settings &rarr;
            Subscription once you're back in.
          </p>
        ) : (
          <p>
            We've emailed you a welcome message with everything you need. Sign in with the address you signed
            up with — we'll email you a code each time, so there's no password to set up.
          </p>
        )}
        <a className="buy-go" href={me ? "/instances" : "/"} aria-disabled={settingUp || undefined} style={settingUp ? { pointerEvents: "none", opacity: 0.6 } : undefined}>
          {settingUp ? "Setting your plan back up…" : me ? "Go to your dials" : "Sign in"}
        </a>
        {me ? (
          gaveUp ? (
            <p className="buy-muted">
              It's taking longer than usual. If you land on the trial-ended screen, give it a moment and try again —
              nothing is lost.
            </p>
          ) : null
        ) : (
          <p className="buy-muted">
            If the email hasn't arrived in a few minutes, check your spam folder before getting in touch —
            it's almost always there.
          </p>
        )}
      </section>
    </main>
  );
}
